import { useEffect, useMemo, useState } from 'react';
import { CloudSun, DollarSign, Gauge, Pause, Play, TriangleAlert, Users } from 'lucide-react';
import { MapViewport, type MapMarker } from '../components/MapViewport';
import { forecastLayer } from '../map/data/demoLayers';
import type { LayerManifest, Position, SpatialFeature } from '../map/core/types';
import { Panel, Segmented } from '../components/ui';

const TRACK: Position[] = [
  [-55, 17], [-60, 19], [-65, 21], [-70, 23], [-75, 26], [-78, 29],
];
const START = Date.parse('2025-05-20T00:00:00Z');
const END = Date.parse('2025-05-25T00:00:00Z');

export function ForecastPage() {
  const [domain, setDomain] = useState('Weather');
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(28);
  const [speed, setSpeed] = useState('1x');
  const [view, setView] = useState('Timeline');
  const [model, setModel] = useState('GFS');
  const [resolution, setResolution] = useState('9 km');

  const currentTime = useMemo(
    () => new Date(START + (END - START) * progress / 100).toISOString(),
    [progress],
  );
  const stormPosition = useMemo(() => interpolateTrack(TRACK, progress / 100), [progress]);
  const activeForecastLayer = useMemo(() => buildForecastLayer(progress, stormPosition), [progress, stormPosition]);
  const forecastMarkers = useMemo<MapMarker[]>(() => [
    { id: 'eye', longitude: stormPosition[0], latitude: stormPosition[1], tone: 'red', pulse: true, glyph: '◉', label: 'Hurricane eye' },
    { id: 'miami', longitude: -80.19, latitude: 25.76, tone: 'orange', glyph: '●', label: 'Miami' },
    { id: 'havana', longitude: -82.37, latitude: 23.11, tone: 'yellow', glyph: '●', label: 'Havana' },
    { id: 'san-juan', longitude: -66.11, latitude: 18.47, tone: 'purple', glyph: '●', label: 'San Juan' },
  ], [stormPosition]);

  useEffect(() => {
    if (!playing) return undefined;
    const multiplier = Number.parseFloat(speed);
    const timer = window.setInterval(() => {
      setProgress((value) => {
        const next = value + 0.35 * multiplier;
        if (next >= 100) {
          setPlaying(false);
          return 100;
        }
        return next;
      });
    }, 160);
    return () => window.clearInterval(timer);
  }, [playing, speed]);

  const category = progress < 15 ? 'Tropical Storm' : progress < 35 ? 'Category 1' : progress < 58 ? 'Category 3' : 'Category 4';
  const windSpeed = Math.round(95 + progress * 1.55);
  const probability = Math.min(96, Math.round(42 + progress * 0.72));

  return (
    <div className="forecast-layout">
      <div className="forecast-tabs">
        {[
          ['Weather', CloudSun],
          ['Economy', DollarSign],
          ['Population', Users],
          ['Risk', TriangleAlert],
        ].map(([label, Icon]) => <button type="button" key={label as string} className={domain === label ? 'is-active' : ''} onClick={() => setDomain(label as string)}><Icon size={16} />{label as string}</button>)}
      </div>
      <MapViewport
        markers={forecastMarkers}
        layers={domain === 'Weather' ? [activeForecastLayer] : []}
        initialCamera={{ longitude: -67, latitude: 22, height: 5_000_000, pitch: -90 }}
        time={{ mode: 'forecast', currentTime, playbackSpeed: Number.parseFloat(speed) }}
        className="forecast-map"
      >
        <div className="forecast-model-controls">
          <select value={model} onChange={(event) => setModel(event.target.value)}><option>GFS</option><option>ECMWF</option><option>ICON</option></select>
          <select value={resolution} onChange={(event) => setResolution(event.target.value)}><option>9 km</option><option>18 km</option><option>25 km</option></select>
        </div>
        {domain === 'Weather' ? (
          <div className="wind-legend"><span>Wind Speed (km/h)</span><i /><div><small>0</small><small>50</small><small>100</small><small>150</small><small>200</small><small>250+</small></div></div>
        ) : (
          <div className="map-engine-message" role="status">{domain} model provider is not configured. No synthetic result was substituted.</div>
        )}
      </MapViewport>
      <aside className="forecast-rail">
        <Panel title={<span className="eyebrow">Hurricane forecast</span>} action={<span className="pill pill--red">Forecast sandbox</span>}>
          <h2>{category}</h2>
          <div className="forecast-summary"><div><small>Valid time</small><strong className="text-yellow">{new Date(currentTime).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}</strong></div><div><small>Wind speed</small><strong>{windSpeed} km/h</strong></div><div><small>Probability</small><strong>{probability}%</strong></div></div>
          <div className="impact-timeline">
            {[
              ['Tropical Storm','May 20, 08:00','green',8],
              ['Hurricane Category 1','May 20, 20:00','yellow',22],
              ['Hurricane Category 3','May 21, 14:00','orange',44],
              ['Hurricane Category 4','May 22, 02:00','red',62],
              ['Potential Landfall','May 24, 02:00','purple',86],
            ].map(([label,time,tone,threshold]) => <button type="button" key={label as string} className={progress >= Number(threshold) ? 'is-active' : ''} onClick={() => setProgress(Number(threshold))}><i className={`severity-dot severity-dot--${tone}`} /><span>{label as string}</span><time>{time as string}</time></button>)}
          </div>
        </Panel>
        <Panel title={<span className="eyebrow">Key metrics</span>}>
          <div className="forecast-key-grid"><div><small>Max wind speed</small><strong>{windSpeed} <em>km/h</em></strong><span>forecast value</span></div><div><small>Expected rainfall</small><strong>{Math.round(180 + progress * 2.2)} <em>mm</em></strong><span>ensemble median</span></div><div><small>Storm surge</small><strong>{(1.4 + progress * 0.023).toFixed(1)} <em>m</em></strong><span>modeled range</span></div><div><small>Affected area</small><strong>{Math.round(55_000 + progress * 700).toLocaleString()} <em>km²</em></strong><span>generalized</span></div></div>
        </Panel>
      </aside>
      <Panel className="forecast-timeline" title={<span className="eyebrow">Simulation timeline</span>} action={<Segmented items={['Timeline','Table','Map']} value={view} onChange={setView} />}>
        <div className="simulation-player"><button type="button" onClick={() => setPlaying((value) => !value)} aria-label={playing ? 'Pause' : 'Play'}>{playing ? <Pause size={19} fill="currentColor" /> : <Play size={19} fill="currentColor" />}</button><select value={speed} onChange={(event) => setSpeed(event.target.value)}><option>0.5x</option><option>1x</option><option>2x</option><option>4x</option></select></div>
        {view === 'Timeline' && (
          <div className="simulation-track">
            <div className="simulation-track__labels">{['May 20 00:00','May 20 12:00','May 21 00:00','May 21 12:00','May 22 00:00','May 22 12:00','May 23 00:00','May 24 00:00','May 25 00:00'].map((item,index)=><button type="button" key={item} className={Math.abs(progress - index / 8 * 100) < 7 ? 'is-active' : ''} onClick={() => setProgress(index / 8 * 100)}>{item}</button>)}</div>
            <input aria-label="Forecast valid time" type="range" min="0" max="100" value={progress} onChange={(event) => setProgress(Number(event.target.value))} />
            <div className="storm-phases"><i className="phase phase--green" /><i className="phase phase--yellow" /><i className="phase phase--orange" /><i className="phase phase--red" /><i className="phase phase--purple" /></div>
          </div>
        )}
        {view === 'Table' && <div className="forecast-run-table"><span>Valid time</span><strong>{currentTime}</strong><span>Category</span><strong>{category}</strong><span>Center</span><strong>{stormPosition[1].toFixed(2)}°, {stormPosition[0].toFixed(2)}°</strong><span>Probability</span><strong>{probability}%</strong></div>}
        {view === 'Map' && <div className="forecast-run-table"><span>Spatial output</span><strong>Track and current center are synchronized with the map above.</strong><span>Renderer state</span><strong>{model} · {resolution}</strong></div>}
        <div className="model-run"><Gauge size={16} /><span>Model run<strong>May 20, 10:00 UTC · {model}</strong></span></div>
      </Panel>
    </div>
  );
}

function interpolateTrack(track: Position[], ratio: number): Position {
  const scaled = Math.max(0, Math.min(1, ratio)) * (track.length - 1);
  const index = Math.min(track.length - 2, Math.floor(scaled));
  const t = scaled - index;
  return [
    track[index][0] + (track[index + 1][0] - track[index][0]) * t,
    track[index][1] + (track[index + 1][1] - track[index][1]) * t,
    0,
  ];
}

function buildForecastLayer(progress: number, center: Position): LayerManifest {
  const completedIndex = Math.max(1, Math.ceil((progress / 100) * (TRACK.length - 1)));
  const trackCoordinates = [...TRACK.slice(0, completedIndex), center];
  const features: SpatialFeature[] = [
    {
      type: 'Feature',
      id: 'storm-track',
      properties: { name: 'Category forecast track', color: '#ff4d4f', validFrom: '2025-05-20T00:00:00Z', validTo: '2025-05-25T00:00:00Z' },
      geometry: { type: 'LineString', coordinates: trackCoordinates },
    },
    {
      type: 'Feature',
      id: 'storm-center',
      properties: { name: 'Forecast center', label: 'Forecast center', color: '#ff4d4f', validFrom: '2025-05-20T00:00:00Z', validTo: '2025-05-25T00:00:00Z' },
      geometry: { type: 'Point', coordinates: center },
    },
  ];
  return {
    ...forecastLayer,
    data: features,
    metadata: { ...forecastLayer.metadata, observedAt: new Date(START).toISOString() },
  };
}
